import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonalizationItemDetailsModal } from './PersonalizationItemDetailsModal';
import Button, { ButtonType } from 'antd/lib/button';
import { Typography } from 'antd';
import { useFloorplanAdd } from '..';
import { PersonalizationItemFormValue } from './PersonalizationItemFormContent';

const { Text } = Typography;

interface Texts {
  button?: string;
  modalTitle?: string;
  modalSaveBtn?: string;
  modalCloseBtn?: string;
}

interface Props {
  communityId: string;
  homeId: string;
  projectId: string;
  stepId: string;
  itemId: string;
  type?: ButtonType;
  onSubmit: ((args: PersonalizationItemFormValue) => any) | false;
  disabled?: boolean;
  texts?: Texts;
  icon?: React.ReactNode;
  iconOnly?: boolean;
  buttonClassName?: string;
}

const useSubmit = (
  onSubmit: ((args: PersonalizationItemFormValue) => any) | false,
  setOpenModal: (value: boolean) => any,
  homeId: string,
  projectId: string,
  stepId: string,
) => {
  const defaultAdd = useFloorplanAdd(homeId, stepId, projectId);

  return (args: PersonalizationItemFormValue) => {
    onSubmit ? onSubmit(args) : defaultAdd(args);
    setOpenModal(false);
  };
};

export function getPersonalizationItemInitialValue(
  itemId: string | undefined,
): PersonalizationItemFormValue {
  return {
    id: null!,
    itemId: itemId || null!,
    notes: null!,
    suboptionId: null!,
    attachments: [],
  };
}

export function AddPersonalizationItemButton(props: Props) {
  const {
    itemId,
    type,
    homeId,
    projectId,
    disabled,
    texts,
    stepId,
    onSubmit,
    iconOnly,
    icon,
    buttonClassName,
  } = props;

  const { button, modalSaveBtn, modalCloseBtn, modalTitle } = texts ?? {};
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const value = getPersonalizationItemInitialValue(itemId);
  const submit = useSubmit(onSubmit, setOpenModal, homeId, projectId, stepId);

  const buttonLabel = button ?? t('personalization.item.add');
  return (
    <>
      <Button
        type={type || 'primary'}
        disabled={disabled}
        onClick={() => setOpenModal(true)}
        icon={icon}
        className={buttonClassName}
      >
        {!iconOnly && <Text strong>{buttonLabel}</Text>}
      </Button>
      {openModal && (
        <PersonalizationItemDetailsModal
          initialValue={value}
          texts={{
            saveBtn:
              modalSaveBtn ?? t('personalization.item.details.addButton'),
            title: modalTitle ?? t('personalization.item.details.addTitle'),
            closeBtn: modalCloseBtn,
          }}
          onClose={() => setOpenModal(false)}
          onSubmit={submit}
          homeId={homeId}
          projectId={projectId}
        />
      )}
    </>
  );
}
